/**
 * Service for managing saved table views (backend API-based)
 */
import api from './api';

export interface ListViewData {
  columnOrder?: string[];
  visibleColumns?: string[];
  columnWidths?: Record<string, number>;
  sortBy?: string | null;
  sortOrder?: 'asc' | 'desc';
  [key: string]: any;
}

export interface ListView {
  id: string;
  name: string;
  type: 'private' | 'public';
  list_name: string;
  data: ListViewData;
  created_at?: string;
  updated_at?: string;
}

/**
 * Get all saved views for a list type
 */
export const getListViews = async (
  listName: string,
  tenantId: string | number
): Promise<ListView[]> => {
  try {
    const { data } = await api.get(`/list-views/by-user/${listName}`, {
      params: { tenant_id: tenantId },
    });
    return Array.isArray(data) ? data : data.list_views || [];
  } catch (error) {
    console.error('Error loading views from server:', error);
    throw error;
  }
};

/**
 * Save a new view
 */
export const saveListView = async (
  listName: string,
  tenantId: string | number,
  view: {
    name: string;
    type: 'private' | 'public';
    data: ListViewData;
  }
): Promise<ListView> => {
  try {
    const { data } = await api.post(`/list-views/`, {
      name: view.name,
      type: view.type,
      list_name: listName,
      data: view.data,
    }, {
      params: { tenant_id: tenantId },
    });
    return data;
  } catch (error) {
    console.error('Error saving view to server:', error);
    throw error;
  }
};

/**
 * Update an existing view
 */
export const updateListView = async (
  viewId: string | number,
  tenantId: string | number,
  updates: { data: ListViewData }
): Promise<ListView> => {
  try {
    const { data } = await api.patch(`/list-views/${viewId}`, updates, {
      params: { tenant_id: tenantId },
    });
    return data;
  } catch (error) {
    console.error('Error updating view on server:', error);
    throw error;
  }
};

/**
 * Delete a view
 */
export const deleteListView = async (
  viewId: string | number,
  tenantId: string | number
): Promise<void> => {
  try {
    await api.delete(`/list-views/${viewId}`, {
      params: { tenant_id: tenantId },
    });
  } catch (error) {
    console.error('Error deleting view from server:', error);
    throw error;
  }
};

/**
 * Get default view (first one marked as default)
 */
export const getDefaultView = async (
  listName: string,
  tenantId: string | number
): Promise<ListView | null> => {
  try {
    const views = await getListViews(listName, tenantId);
    return views.length > 0 ? views[0] : null;
  } catch (error) {
    console.error('Error getting default view:', error);
    return null;
  }
};

/**
 * Set a view as default
 */
export const setDefaultView = async (
  viewId: string | number,
  tenantId: string | number
): Promise<ListView> => {
  try {
    const { data } = await api.patch(`/list-views/${viewId}`, {
      is_default: true,
    }, {
      params: { tenant_id: tenantId },
    });
    return data;
  } catch (error) {
    console.error('Error setting default view:', error);
    throw error;
  }
};

/**
 * Save the latest chosen view ID to localStorage
 */
export const saveLatestChosenView = (tenantId: string, viewId: string): void => {
  try {
    const key = `latest_view_${tenantId}`;
    localStorage.setItem(key, viewId);
    console.log(`[listViewService] Saved latest chosen view: ${viewId}`);
  } catch (error) {
    console.error('Error saving latest chosen view:', error);
  }
};

/**
 * Get the latest chosen view ID from localStorage
 */
export const getLatestChosenView = (tenantId: string): string | null => {
  try {
    const key = `latest_view_${tenantId}`;
    const viewId = localStorage.getItem(key);
    console.log(`[listViewService] Retrieved latest chosen view: ${viewId}`);
    return viewId;
  } catch (error) {
    console.error('Error retrieving latest chosen view:', error);
    return null;
  }
};

/**
 * Clear the latest chosen view ID from localStorage
 */
export const clearLatestChosenView = (tenantId: string): void => {
  try {
    const key = `latest_view_${tenantId}`;
    localStorage.removeItem(key);
    console.log(`[listViewService] Cleared latest chosen view`);
  } catch (error) {
    console.error('Error clearing latest chosen view:', error);
  }
};
